import React, { useState, useEffect } from "react";
import { collection, onSnapshot, doc, query, where } from "firebase/firestore";
import { db, appId } from "../lib/firebase";
import { formatCurrency } from "../lib/utils";
import {
  LayoutDashboard,
  TrendingUp,
  Clock,
  ArrowRight,
  Euro,
  Cloud,
  Database,
  Calendar,
  Briefcase,
  Lock,
  Crown,
  Hammer,
  HardHat,
  BarChart3,
  Filter,
  CalendarDays,
  PlusCircle,
  UserPlus,
  FileText,
  Zap,
  Download,
  ChevronRight,
  PieChart,
  AlertCircle,
} from "lucide-react";
import Button from "./ui/Button";

// --- DONUT CHART (Inchangé) ---
const DonutChart = ({ data }) => {
  const total = data.reduce((acc, item) => acc + item.value, 0);
  let currentAngle = 0;
  const radius = 40;
  const centerX = 50;
  const centerY = 50;
  if (total === 0)
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-400">
        <PieChart size={48} className="mb-2 opacity-20" />
        <p className="text-sm">Pas de données sur cette période</p>
      </div>
    );
  return (
    <div className="flex flex-col md:flex-row items-center gap-8 p-6">
      <div className="w-48 h-48 relative shrink-0">
        <svg
          viewBox="0 0 100 100"
          className="transform -rotate-90 w-full h-full overflow-visible"
        >
          {data.map((item, index) => {
            if (item.value === 0) return null;
            const percentage = item.value / total;
            const strokeDasharray = `${percentage * 2 * Math.PI * radius} ${
              2 * Math.PI * radius
            }`;
            const strokeDashoffset = -currentAngle * 2 * Math.PI * radius;
            currentAngle += percentage;
            return (
              <circle
                key={index}
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="transparent"
                stroke={item.color}
                strokeWidth="12"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-1000 ease-out hover:opacity-80 cursor-pointer"
              />
            );
          })}
          <g className="transform rotate-90 origin-center">
            <text
              x="50%"
              y="45%"
              textAnchor="middle"
              dy=".3em"
              className="text-[10px] fill-slate-400 font-bold"
            >
              TOTAL CA
            </text>
            <text
              x="50%"
              y="60%"
              textAnchor="middle"
              dy=".3em"
              className="text-[14px] fill-slate-800 font-extrabold"
            >
              {formatCurrency(total)}
            </text>
          </g>
        </svg>
      </div>
      <div className="flex-1 w-full space-y-4">
        <h4 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
          Répartition du Chiffre d'Affaires
        </h4>
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              ></div>
              <span className="text-sm font-medium text-slate-600">
                {item.label}
              </span>
            </div>
            <div className="text-right">
              <div className="font-bold text-slate-800">
                {formatCurrency(item.value)}
              </div>
              <div className="text-xs text-slate-400 font-medium">
                {Math.round((item.value / total) * 100)}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Dashboard = ({ user, activeCompanyId, onNavigate, onCreateDocument, userRole }) => {
  const [stats, setStats] = useState({
    ca: 0,
    caLabor: 0,
    caMaterial: 0,
    pendingQuotes: 0,
    invoicesCount: 0,
  });
  const [weeklyTasks, setWeeklyTasks] = useState([]);
  const [companyPlan, setCompanyPlan] = useState("free");
  
  // Vérifier si l'utilisateur peut accéder aux données financières
  const canAccessFinancial = ['owner', 'admin', 'manager', 'editor'].includes(userRole || 'viewer');
  const [caView, setCaView] = useState("total");
  const [periodFilter, setPeriodFilter] = useState("all"); // all, week, month, quarter, year, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [siteTasks, setSiteTasks] = useState([]);
  const [generalEvents, setGeneralEvents] = useState([]);
  const [useLocalMode, setUseLocalMode] = useState(false);
  const [allInvoices, setAllInvoices] = useState([]);
  const [allInvoicesRaw, setAllInvoicesRaw] = useState([]); // Toutes les factures brutes
  const [allQuotes, setAllQuotes] = useState([]); // Tous les devis
  const [userProfile, setUserProfile] = useState(null);
  const [expiredQuotes, setExpiredQuotes] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);

  const hour = new Date().getHours();
  const greeting = hour < 18 ? "Bonjour" : "Bonsoir";

  useEffect(() => {
    if (!user || !activeCompanyId) return;
    // Charger le plan de l'ENTREPRISE (pas du user)
    onSnapshot(doc(db, "companies", activeCompanyId), (docSnap) => {
      if (docSnap.exists()) {
        setCompanyPlan(docSnap.data().plan || "free");
      } else if (user.isAnonymous) {
        setCompanyPlan("business_monthly");
      }
    });
    
    // Charger le profil utilisateur depuis artifacts/settings
    const unsubProfile = onSnapshot(
      doc(db, "artifacts", appId, "users", user.uid, "settings", "general"),
      (docSnap) => {
        if (docSnap.exists() && docSnap.data().profile) {
          setUserProfile(docSnap.data().profile);
        }
      }
    );

    const unsub1 = onSnapshot(
      collection(db, 'companies', activeCompanyId, 'invoices'),
      (s) => {
        let totalCA = 0;
        let totalLabor = 0;
        let totalMaterial = 0;
        const activity = [];
        const invoicesData = [];
        s.docs.forEach((doc) => {
          const data = doc.data();
          invoicesData.push(data);
          if (data.status === "Payée") {
            totalCA += data.total || 0;
            if (data.sections) {
              let invoiceLabor = 0;
              let invoiceMaterial = 0;
              data.sections.forEach((sec) => {
                if (sec.items) {
                  sec.items.forEach((item) => {
                    const linePrice = (item.qty || 0) * (item.price || 0);
                    const itemVAT = item.vat !== undefined ? item.vat : 20;
                    const lineTTC = linePrice * (1 + itemVAT / 100);
                    if (item.nature === "labor") invoiceLabor += lineTTC;
                    else invoiceMaterial += lineTTC;
                  });
                }
              });
              const rawTotal = invoiceLabor + invoiceMaterial;
              if (rawTotal > 0) {
                const ratio = data.total / rawTotal;
                totalLabor += invoiceLabor * ratio;
                totalMaterial += invoiceMaterial * ratio;
              }
            }
            activity.push({
              type: "invoice",
              date: data.date,
              client: data.clientName,
              amount: data.total,
              status: data.status,
              id: doc.id,
            });
          }
        });
        setAllInvoices(invoicesData);
        setAllInvoicesRaw(invoicesData); // Sauvegarder toutes les factures
        
        // Détecter les factures en retard (non payées après 30 jours)
        const now = new Date();
        const overdue = invoicesData.filter(invoice => {
          if (invoice.status === "Payée") return false;
          if (!invoice.date) return false;
          
          const invoiceDate = new Date(invoice.date.split("/").reverse().join("-"));
          const paymentDeadline = new Date(invoiceDate);
          paymentDeadline.setDate(invoiceDate.getDate() + 30); // Délai de paiement standard
          
          return now > paymentDeadline;
        });
        setOverdueInvoices(overdue);
        
        activity.sort((a, b) => {
          const dateA = a.date
            ? new Date(a.date.split("/").reverse().join("-"))
            : new Date();
          const dateB = b.date
            ? new Date(b.date.split("/").reverse().join("-"))
            : new Date();
          return dateB - dateA;
        });
        setStats((prev) => ({
          ...prev,
          ca: totalCA,
          caLabor: totalLabor,
          caMaterial: totalMaterial,
          invoicesCount: s.size,
        }));
        setRecentActivity(activity.slice(0, 4));
      },
      () => setUseLocalMode(true)
    );

    const unsub2 = onSnapshot(
      collection(db, 'companies', activeCompanyId, 'quotes'),
      (s) => {
        const quotesData = s.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllQuotes(quotesData);
        
        // COMPTEUR CORRIGÉ : Brouillon + En attente
        const pending = s.docs.filter(
          (d) =>
            d.data().status === "En attente" || d.data().status === "Brouillon"
        ).length;
        setStats((prev) => ({ ...prev, pendingQuotes: pending }));
        
        // Détecter les devis expirés (30 jours de validité par défaut)
        const now = new Date();
        const expired = quotesData.filter(quote => {
          if (quote.status !== "En attente" && quote.status !== "Brouillon") return false;
          if (!quote.date) return false;
          
          const quoteDate = new Date(quote.date.split("/").reverse().join("-"));
          const validityDays = 30; // Durée de validité standard
          const expiryDate = new Date(quoteDate);
          expiryDate.setDate(quoteDate.getDate() + validityDays);
          
          return now > expiryDate;
        });
        setExpiredQuotes(expired);
      }
    );

    const unsubSites = onSnapshot(
      collection(db, "companies", activeCompanyId, "sites"),
      (snapshot) => {
        const sites = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSiteTasks(
          sites.flatMap((site) =>
            (site.planning || []).map((t) => ({
              ...t,
              type: "site",
              siteId: site.id,
              siteName: site.name,
              color: "bg-blue-50 text-blue-700 border-blue-100",
            }))
          )
        );
      }
    );
    const unsubGeneral = onSnapshot(
      collection(db, "artifacts", appId, "users", user.uid, "planning"),
      (snapshot) => {
        setGeneralEvents(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            type: "general",
            color: "bg-purple-50 text-purple-700 border-purple-100",
          }))
        );
      }
    );
    return () => {
      unsub1();
      unsub2();
      unsubSites();
      unsubGeneral();
      unsubProfile();
    };
  }, [user, activeCompanyId]);

  useEffect(() => {
    const allTasks = [...siteTasks, ...generalEvents];
    
    // Obtenir la date d'aujourd'hui en format YYYY-MM-DD (sans heure)
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Calculer la date dans 7 jours
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    const nextWeekStr = nextWeek.toISOString().split('T')[0];
    
    const upcoming = allTasks
      .filter((t) => {
        // Comparer uniquement les dates au format YYYY-MM-DD
        const isInDateRange = t.date >= todayStr && t.date <= nextWeekStr;
        
        // Si l'utilisateur est owner/admin/manager/editor, montrer toutes les tâches
        if (canAccessFinancial) {
          return isInDateRange;
        }
        
        // Pour les ouvriers (viewer/worker), ne montrer que leurs tâches assignées
        if (!t.assignedTo || t.assignedTo.length === 0) {
          return false; // Pas assignée = ne pas montrer
        }
        
        return isInDateRange && t.assignedTo.some(member => 
          member.email === user.email || member === user.email
        );
      })
      .sort(
        (a, b) =>
          new Date(a.date + ' ' + (a.time || "00:00")) - new Date(b.date + ' ' + (b.time || "00:00"))
      );
    
    setWeeklyTasks(upcoming);
  }, [siteTasks, generalEvents, canAccessFinancial, user]);

  const isBusiness = companyPlan && companyPlan.includes("business");
  
  // Fonction pour filtrer les factures par période
  const getFilteredInvoices = () => {
    if (periodFilter === "all") return allInvoicesRaw;
    
    const now = new Date();
    const filtered = allInvoicesRaw.filter(inv => {
      if (!inv.date || inv.status !== "Payée") return false;
      
      const invDate = new Date(inv.date.split("/").reverse().join("-"));
      
      // Filtre personnalisé
      if (periodFilter === "custom") {
        if (!customStartDate && !customEndDate) return true;
        
        const start = customStartDate ? new Date(customStartDate) : new Date("1900-01-01");
        const end = customEndDate ? new Date(customEndDate) : new Date("2100-12-31");
        end.setHours(23, 59, 59, 999); // Inclure toute la journée de fin
        
        return invDate >= start && invDate <= end;
      }
      
      switch (periodFilter) {
        case "week": {
          const weekAgo = new Date(now);
          weekAgo.setDate(now.getDate() - 7);
          return invDate >= weekAgo;
        }
        case "month": {
          const monthAgo = new Date(now);
          monthAgo.setMonth(now.getMonth() - 1);
          return invDate >= monthAgo;
        }
        case "quarter": {
          const quarterAgo = new Date(now);
          quarterAgo.setMonth(now.getMonth() - 3);
          return invDate >= quarterAgo;
        }
        case "year": {
          const yearAgo = new Date(now);
          yearAgo.setFullYear(now.getFullYear() - 1);
          return invDate >= yearAgo;
        }
        default:
          return true;
      }
    });
    
    return filtered;
  };
  
  // Calculer le CA filtré
  const filteredStats = (() => {
    const filtered = getFilteredInvoices();
    let totalCA = 0;
    let totalLabor = 0;
    let totalMaterial = 0;
    
    filtered.forEach((data) => {
      totalCA += data.total || 0;
      if (data.sections) {
        let invoiceLabor = 0;
        let invoiceMaterial = 0;
        data.sections.forEach((sec) => {
          if (sec.items) {
            sec.items.forEach((item) => {
              const linePrice = (item.qty || 0) * (item.price || 0);
              const itemVAT = item.vat !== undefined ? item.vat : 20;
              const lineTTC = linePrice * (1 + itemVAT / 100);
              if (item.nature === "labor") invoiceLabor += lineTTC;
              else invoiceMaterial += lineTTC;
            });
          }
        });
        const rawTotal = invoiceLabor + invoiceMaterial;
        if (rawTotal > 0) {
          const ratio = data.total / rawTotal;
          totalLabor += invoiceLabor * ratio;
          totalMaterial += invoiceMaterial * ratio;
        }
      }
    });
    
    return { ca: totalCA, caLabor: totalLabor, caMaterial: totalMaterial };
  })();
  
  const handleExportAccounting = () => {
    if (allInvoices.length === 0) {
      alert("Aucune facture.");
      return;
    }
    const headers = ["Numéro", "Date", "Client", "Total TTC", "Statut"];
    const rows = allInvoices.map((inv) => [
      inv.number,
      inv.date,
      `"${inv.clientName}"`,
      inv.total.toFixed(2).replace(".", ","),
      inv.status,
    ]);
    const csvContent = [
      headers.join(";"),
      ...rows.map((row) => row.join(";")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `export_comptable_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  const getCaCardStyle = () => {
    switch (caView) {
      case "labor":
        return {
          bg: "bg-gradient-to-br from-indigo-500 to-indigo-700",
          text: "text-white",
          iconBg: "bg-white/20 text-white",
          title: "CA Main d'oeuvre",
        };
      case "material":
        return {
          bg: "bg-gradient-to-br from-orange-500 to-red-600",
          text: "text-white",
          iconBg: "bg-white/20 text-white",
          title: "CA Matériel",
        };
      default:
        return {
          bg: "bg-gradient-to-br from-slate-800 to-slate-900",
          text: "text-white",
          iconBg: "bg-white/20 text-white",
          title: "Chiffre d'Affaires",
        };
    }
  };
  const caStyle = getCaCardStyle();

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 animate-page pb-24">
      {/* Header (Inchangé) */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div className="flex items-center gap-4">
          {/* Photo de profil */}
          <div className="relative">
            {userProfile?.photoUrl ? (
              <img 
                src={userProfile.photoUrl} 
                alt="Profile" 
                className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl border-4 border-white shadow-lg">
                {userProfile?.firstName ? userProfile.firstName.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase() || "U"}
              </div>
            )}
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
          </div>
          
          <div>
            <p className="text-slate-500 font-medium text-sm mb-1">{greeting},</p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
              {userProfile?.firstName && userProfile?.lastName 
                ? `${userProfile.firstName} ${userProfile.lastName}` 
                : user.email?.split("@")[0] || "Artisan"}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canAccessFinancial && (
            <>
              <div className="flex flex-col gap-2">
                <div className="flex bg-white p-1 rounded-xl shadow-sm border border-slate-200">
                  {[
                    { id: "all", label: "Tout" },
                    { id: "week", label: "7j" },
                    { id: "month", label: "30j" },
                    { id: "quarter", label: "3 mois" },
                    { id: "year", label: "1 an" }
                  ].map(period => (
                    <button
                      key={period.id}
                      onClick={() => {
                        setPeriodFilter(period.id);
                        setShowCustomDates(false);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        periodFilter === period.id
                          ? "bg-blue-500 text-white"
                          : "text-slate-500 hover:bg-slate-50"
                      }`}
                    >
                      {period.label}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setPeriodFilter("custom");
                      setShowCustomDates(!showCustomDates);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      periodFilter === "custom"
                        ? "bg-purple-500 text-white"
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    📅 Personnalisé
                  </button>
                </div>
                
                {showCustomDates && periodFilter === "custom" && (
                  <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200 animate-in slide-in-from-top-2">
                    <div className="flex gap-3 items-center">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-600">Début</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-600">Fin</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                        />
                      </div>
                      {(customStartDate || customEndDate) && (
                        <button
                          onClick={() => {
                            setCustomStartDate("");
                            setCustomEndDate("");
                          }}
                          className="mt-5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-100 transition"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={handleExportAccounting}
                className="bg-white text-slate-700 px-4 py-2 rounded-xl shadow-sm border border-slate-200 flex items-center gap-2 text-xs font-bold hover:bg-slate-50 transition"
              >
                <Download size={16} className="text-green-600" /> Export Compta
              </button>
            </>
          )}
          <div className="flex items-center gap-2 bg-white p-1.5 rounded-xl shadow-sm border border-slate-200">
            <span
              className={`px-2 py-1 text-[10px] md:text-xs rounded-lg flex items-center gap-1.5 font-bold ${
                useLocalMode
                  ? "bg-orange-50 text-orange-600"
                  : "bg-green-50 text-green-600"
              }`}
            >
              {useLocalMode ? <Database size={12} /> : <Cloud size={12} />}{" "}
              {useLocalMode ? "Hors ligne" : "Connecté"}
            </span>
          </div>
        </div>
      </div>

      {/* Alertes et Notifications */}
      {canAccessFinancial && (expiredQuotes.length > 0 || overdueInvoices.length > 0) && (
        <div className="mb-8 space-y-3">
          {expiredQuotes.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-4 rounded-xl flex items-start gap-3 shadow-sm">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle size={20} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-amber-900 mb-1">
                  ⚠️ {expiredQuotes.length} devis expiré{expiredQuotes.length > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-amber-700">
                  {expiredQuotes.slice(0, 3).map(q => q.clientName).join(", ")}
                  {expiredQuotes.length > 3 && ` et ${expiredQuotes.length - 3} autre(s)`}
                </p>
              </div>
              <button
                onClick={() => onNavigate("gestion")}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-xs font-bold hover:bg-amber-700 transition"
              >
                Voir
              </button>
            </div>
          )}
          
          {overdueInvoices.length > 0 && (
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-4 rounded-xl flex items-start gap-3 shadow-sm">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-1">
                  🔴 {overdueInvoices.length} facture{overdueInvoices.length > 1 ? 's' : ''} en retard
                </h3>
                <p className="text-sm text-red-700">
                  {overdueInvoices.slice(0, 3).map(inv => inv.clientName).join(", ")}
                  {overdueInvoices.length > 3 && ` et ${overdueInvoices.length - 3} autre(s)`}
                </p>
              </div>
              <button
                onClick={() => onNavigate("gestion")}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700 transition"
              >
                Relancer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Actions Rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {canAccessFinancial && (
          <button
            onClick={() => onCreateDocument(null, "quote")}
            className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition flex flex-col items-center gap-2 group"
          >
            <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FileText size={20} />
            </div>
            <span className="text-xs font-bold text-slate-600">
              Nouveau Devis
            </span>
          </button>
        )}
        {canAccessFinancial && (
          <button
            onClick={() => onCreateDocument(null, "invoice")}
            className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-green-300 transition flex flex-col items-center gap-2 group"
          >
            <div className="w-10 h-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <Euro size={20} />
            </div>
            <span className="text-xs font-bold text-slate-600">
              Nouvelle Facture
            </span>
          </button>
        )}
        {canAccessFinancial && (
          <button
            onClick={() => onNavigate("clients")}
            className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-purple-300 transition flex flex-col items-center gap-2 group"
          >
            <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <UserPlus size={20} />
            </div>
            <span className="text-xs font-bold text-slate-600">
              Ajouter Client
            </span>
          </button>
        )}
        {canAccessFinancial && (
          <button
            onClick={() => onNavigate("chantiers")}
            className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-orange-300 transition flex flex-col items-center gap-2 group"
          >
            <div className="w-10 h-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <HardHat size={20} />
            </div>
            <span className="text-xs font-bold text-slate-600">
              Nouveau Chantier
            </span>
          </button>
        )}
      </div>

      {canAccessFinancial && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
          {/* CARTE CA */}
          <div
            className={`p-6 rounded-2xl shadow-lg transition-all duration-300 relative overflow-hidden group ${caStyle.bg}`}
            title={periodFilter !== "all" ? `CA filtré sur la période sélectionnée` : "CA total"}
          >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-10 -mt-10 blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          <div className="flex justify-between items-start mb-6 relative z-10">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-inner ${caStyle.iconBg}`}
            >
              {caView === "labor" ? (
                <HardHat size={24} />
              ) : caView === "material" ? (
                <Hammer size={24} />
              ) : (
                <TrendingUp size={24} />
              )}
            </div>
            <select
              value={caView}
              onChange={(e) => setCaView(e.target.value)}
              className="bg-white/20 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-wide border border-white/30 outline-none cursor-pointer hover:bg-white/30 transition"
            >
              <option value="total" className="text-slate-800">
                Total
              </option>
              <option value="labor" className="text-slate-800">
                M. Oeuvre
              </option>
              <option value="material" className="text-slate-800">
                Matériel
              </option>
            </select>
          </div>
          <div className="relative z-10">
            <div
              className={`text-xs font-bold uppercase mb-1 opacity-80 ${caStyle.text}`}
            >
              {caStyle.title}
            </div>
            <div
              className={`text-3xl md:text-4xl font-extrabold tracking-tight ${caStyle.text}`}
            >
              {formatCurrency(
                caView === "total"
                  ? (periodFilter === "all" ? stats.ca : filteredStats.ca)
                  : caView === "labor"
                  ? (periodFilter === "all" ? stats.caLabor : filteredStats.caLabor)
                  : (periodFilter === "all" ? stats.caMaterial : filteredStats.caMaterial)
              )}
            </div>
          </div>
        </div>

        {/* Carte Devis - SANS FLÈCHE, FILTRE "EN ATTENTE" - Uniquement pour les rôles financiers */}
        {canAccessFinancial && (
          <div
            onClick={() => onNavigate("gestion", "En attente")}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 cursor-pointer hover:shadow-lg hover:border-orange-200 transition-all group relative"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Clock size={24} />
              </div>
            </div>
            <div>
              <div className="text-slate-400 text-xs font-bold uppercase mb-1">
                En cours
              </div>
              <div className="text-3xl font-extrabold text-slate-800 tracking-tight">
                {stats.pendingQuotes}{" "}
                <span className="text-base font-normal text-slate-400">
                  devis
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Carte Factures - SANS FLÈCHE - Uniquement pour les rôles financiers */}
        {canAccessFinancial && (
          <div
            onClick={() => onNavigate("gestion")}
            className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 cursor-pointer hover:shadow-lg hover:border-green-200 transition-all group"
          >
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Euro size={24} />
            </div>
          </div>
          <div>
            <div className="text-slate-400 text-xs font-bold uppercase mb-2">
              Derniers paiements
            </div>
            <div className="space-y-2">
              {recentActivity.length > 0 ? (
                recentActivity.map((act, i) => (
                  <div
                    key={i}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onCreateDocument) onCreateDocument(act.id, "invoice");
                    }}
                    className="flex justify-between items-center text-xs p-1.5 -mx-1.5 rounded-lg hover:bg-slate-50 cursor-pointer transition group/item"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"></div>
                      <span className="text-slate-700 truncate font-medium group-hover/item:text-blue-600">
                        {act.client}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                        +{Math.round(act.amount)}€
                      </span>
                      <ChevronRight size={12} className="text-slate-300" />
                    </div>
                  </div>
                ))
              ) : (
                <span className="text-xs text-slate-400 italic">
                  Aucune activité
                </span>
              )}
            </div>
          </div>
          </div>
        )}
        </div>
      )}

      {canAccessFinancial && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <PieChart size={20} className="text-indigo-600" /> Répartition du CA
            </h3>
          </div>
          <DonutChart
            data={[
              { label: "Main d'œuvre", value: stats.caLabor, color: "#4f46e5" },
              { label: "Matériel", value: stats.caMaterial, color: "#f97316" },
            ]}
          />
        </div>
      )}

      <div className="relative bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 text-lg">
            <Calendar size={20} className="text-purple-600" /> Planning de la
            semaine{" "}
            {!isBusiness && (
              <span className="bg-orange-100 text-orange-600 text-[10px] px-2 py-0.5 rounded-full uppercase font-bold flex items-center gap-1">
                <Lock size={10} /> Business
              </span>
            )}
          </h3>
          {isBusiness && (
            <button
              onClick={() => onNavigate("chantiers")}
              className="text-xs font-bold text-purple-600 hover:text-purple-800 transition bg-purple-50 px-3 py-1.5 rounded-lg hover:bg-purple-100"
            >
              Voir tout
            </button>
          )}
        </div>
{isBusiness ? (
          <div className="p-4">
            {weeklyTasks.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                  <Calendar size={32} className="opacity-20" />
                </div>
                <p>Rien de prévu cette semaine.</p>
              </div>
            ) : (
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-3 min-w-max">
                  {(() => {
                    // Créer un tableau des 7 prochains jours
                    const days = [];
                    const today = new Date();
                    for (let i = 0; i < 7; i++) {
                      const date = new Date(today);
                      date.setDate(today.getDate() + i);
                      const dateStr = date.toISOString().split('T')[0];
                      const dayTasks = weeklyTasks.filter(t => t.date === dateStr);
                      days.push({ date, dateStr, tasks: dayTasks });
                    }
                    
                    return days.map((day, idx) => {
                      const isToday = day.dateStr === today.toISOString().split('T')[0];
                      return (
                        <div
                          key={idx}
                          className={`flex-shrink-0 w-36 rounded-xl border-2 overflow-hidden transition-all ${
                            isToday 
                              ? 'border-indigo-500 bg-indigo-50/50 shadow-lg' 
                              : 'border-slate-200 bg-white'
                          }`}
                        >
                          {/* En-tête du jour */}
                          <div className={`p-3 text-center border-b ${
                            isToday 
                              ? 'bg-indigo-600 border-indigo-700' 
                              : 'bg-slate-100 border-slate-200'
                          }`}>
                            <div className={`text-[10px] font-bold uppercase tracking-wide ${
                              isToday ? 'text-indigo-100' : 'text-slate-500'
                            }`}>
                              {day.date.toLocaleDateString("fr-FR", { weekday: "short" })}
                            </div>
                            <div className={`text-2xl font-extrabold ${
                              isToday ? 'text-white' : 'text-slate-800'
                            }`}>
                              {day.date.getDate()}
                            </div>
                          </div>
                          
                          {/* Liste des tâches du jour */}
                          <div className="p-2 space-y-2 min-h-[120px] max-h-[300px] overflow-y-auto">
                            {day.tasks.length === 0 ? (
                              <div className="flex items-center justify-center h-24 text-xs text-slate-400 italic">
                                Aucune tâche
                              </div>
                            ) : (
                              day.tasks.map((task, taskIdx) => (
                                <button
                                  key={taskIdx}
                                  onClick={() => {
                                    if (task.type === 'site' && task.siteId) {
                                      // Naviguer vers le chantier spécifique
                                      onNavigate('chantiers', task.siteId);
                                    } else {
                                      // Pour les événements généraux, aller à l'agenda
                                      onNavigate('chantiers');
                                    }
                                  }}
                                  className={`w-full text-left p-2 rounded-lg border transition-all hover:shadow-md hover:scale-105 cursor-pointer ${
                                    task.type === "general"
                                      ? "bg-purple-50 border-purple-200 hover:bg-purple-100"
                                      : "bg-blue-50 border-blue-200 hover:bg-blue-100"
                                  }`}
                                >
                                  {task.time && (
                                    <div className="flex items-center gap-1 mb-1">
                                      <Clock size={10} className={task.type === "general" ? "text-purple-600" : "text-blue-600"} />
                                      <span className={`text-[9px] font-bold ${
                                        task.type === "general" ? "text-purple-700" : "text-blue-700"
                                      }`}>
                                        {task.time}
                                      </span>
                                    </div>
                                  )}
                                  <div className={`text-xs font-bold mb-1 line-clamp-2 ${
                                    task.type === "general" ? "text-purple-900" : "text-blue-900"
                                  }`}>
                                    {task.title}
                                  </div>
                                  {task.type === "site" && task.siteName && (
                                    <div className="text-[10px] text-slate-600 truncate flex items-center gap-1">
                                      <HardHat size={10} />
                                      {task.siteName}
                                    </div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center bg-gradient-to-b from-slate-50 to-white min-h-[250px]">
            <div className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center mb-4 text-orange-500 relative">
              <div className="absolute inset-0 bg-orange-50 rounded-full animate-pulse opacity-50"></div>
              <Crown
                size={32}
                className="fill-orange-500 text-orange-600 relative z-10"
              />
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm border border-slate-100">
                <Lock size={12} className="text-slate-400" />
              </div>
            </div>
            <h4 className="font-bold text-lg text-slate-800 mb-2">
              Fonctionnalité Business
            </h4>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto text-xs leading-relaxed">
              Débloquez la vue planning et le suivi de chantier.
            </p>
            <button
              onClick={() => onNavigate("settings")}
              className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold text-xs shadow-lg hover:bg-slate-800 transition transform hover:scale-105 flex items-center gap-2"
            >
              Découvrir
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
